import { useMsal } from '@azure/msal-react'
import {InternalAxiosRequestConfig } from 'axios'
import * as React from 'react'

import { colliderApi } from 'api'
import { tokenRequest } from 'auth/config'

interface ApiProviderProps {
  children: React.ReactNode
}

const clients = [colliderApi]

const getAccessToken = async (
  instance: ReturnType<typeof useMsal>['instance'],
  account: ReturnType<typeof useMsal>['accounts'][number] | undefined,
): Promise<string | null> => {
  if (!account) {
    console.warn('[ApiProvider] No active account found')
    return null
  }

  try {
    const response = await instance.acquireTokenSilent({
      ...tokenRequest,
      account,
    })

    if (!response.accessToken) {
      console.error('[ApiProvider] No access token in response')
      return null
    }

    return response.accessToken
  } catch (error) {
    console.error('[ApiProvider] Failed to acquire token:', error)
    return null
  }
}

const applyAuthorizationHeader = (
  config: InternalAxiosRequestConfig,
  token: string,
): InternalAxiosRequestConfig => {
  config.headers = {
    ...(config.headers ?? {}),
    Authorization: `Bearer ${token}`,
  } as InternalAxiosRequestConfig['headers']
  return config
}

export const ApiProvider = ({ children }: ApiProviderProps) => {
  const { instance, accounts } = useMsal()
  const [isReady, setIsReady] = React.useState(false)

  React.useEffect(() => {
    let isMounted = true
    const cleanupHandlers: Array<() => void> = []

    const setupInterceptors = async () => {
      cleanupHandlers.splice(0).forEach(cleanup => cleanup())

      const activeAccount = accounts[0]

      for (const client of clients) {
        const requestInterceptorId = client.client.interceptors.request.use(async config => {
          try {
            const token = await getAccessToken(instance, activeAccount)

            if (!token) {
              console.warn('[ApiProvider] No valid token available for request')
              return config
            }

            return applyAuthorizationHeader(config, token)
          } catch (error) {
            console.error('[ApiProvider] Error in request interceptor:', error)
            return config
          }
        })

        const responseInterceptorId = client.client.interceptors.response.use(
          response => response,
          error => Promise.reject(error),
        )

        cleanupHandlers.push(() => client.client.interceptors.request.eject(requestInterceptorId))
        cleanupHandlers.push(() => client.client.interceptors.response.eject(responseInterceptorId))
      }

      if (isMounted) setIsReady(true)
    }

    setIsReady(false)
    void setupInterceptors()

    return () => {
      isMounted = false
      cleanupHandlers.splice(0).forEach(cleanup => cleanup())
    }
  }, [accounts, instance])

  if (!isReady) return null

  return <>{children}</>
}